import api from './api';

// TypeScript Interfaces
export interface LearningObjective {
  objectiveId: string;
  description: string;
  completed: boolean;
  completedAt?: Date;
}

export interface Section {
  sectionId: string;
  title: string;
  content: string;
  order: number;
  isEditable: boolean;
  lastEditedBy?: string;
  lastEditedAt?: Date;
}

export interface Reflection {
  question: string;
  response: string;
  wordCount: number;
  lastEditedAt?: Date;
}

export interface Resource {
  title: string;
  url: string;
  type: 'PDF' | 'Video' | 'Article' | 'Book' | 'Tool' | 'Other';
}

export interface DataStrategyModule {
  _id?: string;
  moduleNumber: number;
  title: string;
  description: string;
  status: 'Not Started' | 'In Progress' | 'Under Review' | 'Completed';
  progress: number;
  dueDate?: Date;
  startedAt?: Date;
  completedAt?: Date;
  learningObjectives: LearningObjective[];
  sections: Section[];
  reflections: Reflection[];
  keyTakeaways: string[];
  resources: Resource[];
  assignmentSubmitted: boolean;
  assignmentSubmittedAt?: Date;
  grade: 'A+' | 'A' | 'A-' | 'B+' | 'B' | 'B-' | 'C+' | 'C' | 'C-' | 'D' | 'F' | 'Not Graded';
  instructorFeedback?: string;
  notes?: string;
  tags: string[];
  createdAt?: Date;
  updatedAt?: Date;
  objectivesCompletionRate?: number;
}

export interface ProgressSummary {
  totalModules: number;
  completed: number;
  inProgress: number;
  notStarted: number;
  underReview: number;
  overallProgress: number;
  modules: {
    moduleNumber: number;
    title: string;
    status: string;
    progress: number;
    dueDate?: Date;
  }[];
}

class DataStrategyModulesService {
  // Get all modules
  async getAllModules(): Promise<DataStrategyModule[]> {
    try {
      const response = await api.get('/data-strategy-modules');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching modules:', error);
      throw error;
    }
  }

  // Get single module by number
  async getModule(moduleNumber: number): Promise<DataStrategyModule> {
    try {
      const response = await api.get(`/data-strategy-modules/${moduleNumber}`);
      return response.data.data;
    } catch (error) {
      console.error(`Error fetching module ${moduleNumber}:`, error);
      throw error;
    }
  }

  // Create new module
  async createModule(moduleData: Partial<DataStrategyModule>): Promise<DataStrategyModule> {
    try {
      const response = await api.post('/data-strategy-modules', moduleData);
      return response.data.data;
    } catch (error) {
      console.error('Error creating module:', error);
      throw error;
    }
  }

  // Update module
  async updateModule(moduleNumber: number, moduleData: Partial<DataStrategyModule>): Promise<DataStrategyModule> {
    try {
      const response = await api.put(`/data-strategy-modules/${moduleNumber}`, moduleData);
      return response.data.data;
    } catch (error) {
      console.error(`Error updating module ${moduleNumber}:`, error);
      throw error;
    }
  }

  // Update section
  async updateSection(
    moduleNumber: number, 
    sectionId: string, 
    content: string, 
    lastEditedBy?: string
  ): Promise<DataStrategyModule> {
    try {
      const response = await api.put(
        `/data-strategy-modules/${moduleNumber}/sections/${sectionId}`,
        { content, lastEditedBy }
      );
      return response.data.data;
    } catch (error) {
      console.error(`Error updating section ${sectionId}:`, error);
      throw error;
    }
  }

  // Update learning objective
  async updateObjective(
    moduleNumber: number,
    objectiveId: string,
    completed: boolean
  ): Promise<DataStrategyModule> {
    try {
      const response = await api.put(
        `/data-strategy-modules/${moduleNumber}/objectives/${objectiveId}`,
        { completed }
      );
      return response.data.data;
    } catch (error) {
      console.error(`Error updating objective ${objectiveId}:`, error);
      throw error;
    }
  }

  // Update reflection
  async updateReflection(
    moduleNumber: number,
    reflectionIndex: number,
    response: string
  ): Promise<DataStrategyModule> {
    try {
      const apiResponse = await api.put(
        `/data-strategy-modules/${moduleNumber}/reflections/${reflectionIndex}`,
        { response }
      );
      return apiResponse.data.data;
    } catch (error) {
      console.error(`Error updating reflection ${reflectionIndex}:`, error);
      throw error;
    }
  }

  // Submit assignment
  async submitAssignment(moduleNumber: number): Promise<DataStrategyModule> {
    try {
      const response = await api.post(`/data-strategy-modules/${moduleNumber}/submit`);
      return response.data.data;
    } catch (error) {
      console.error(`Error submitting assignment for module ${moduleNumber}:`, error);
      throw error;
    }
  }

  // Delete module
  async deleteModule(moduleNumber: number): Promise<void> {
    try {
      await api.delete(`/data-strategy-modules/${moduleNumber}`);
    } catch (error) {
      console.error(`Error deleting module ${moduleNumber}:`, error);
      throw error;
    }
  }

  // Get progress summary
  async getProgressSummary(): Promise<ProgressSummary> {
    try {
      const response = await api.get('/data-strategy-modules/progress/summary');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching progress summary:', error);
      throw error;
    }
  }
}

export default new DataStrategyModulesService();
